import React from "react";

/**
 * How long a piece of the summary is — shown beside the Overview heading and
 * the chapter breakdown so a writer can see the length they are working to
 * without pasting the text somewhere else to count it.
 *
 * The fields are Quill HTML, so the markup comes off first. Every tag becomes
 * a SPACE rather than nothing: `<p>one</p><p>two</p>` is two words, and
 * stripping the tags to nothing would make it one ("onetwo").
 */
const stripHtml = (html) => {
  const spaced = String(html ?? "").replace(/<[^>]*>/g, " ");
  // Through the parser for the entities — `&amp;`, `&nbsp;` and the rest are
  // characters in the text, not markup, and counting them raw inflates both
  // numbers. Non-breaking spaces become ordinary ones so they split words.
  const doc = new DOMParser().parseFromString(spaced, "text/html");
  return (doc.body.textContent || "").replace(/ /g, " ");
};

/** Words in [parts], which may be HTML, plain text, null, or a mix. */
export const countWords = (...parts) =>
  parts
    .flat()
    .map(stripHtml)
    .join(" ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

/** Characters in [parts], NOT counting spaces, tabs or line breaks. */
export const countCharacters = (...parts) =>
  parts.flat().map(stripHtml).join("").replace(/\s+/g, "").length;

const format = (n) => n.toLocaleString("en-US");

/**
 * `1,240 words · 6,801 characters`. The character count excludes spaces —
 * spaces are not content, and a count that includes them says nothing about
 * how much was written.
 */
const TextStats = ({ parts, className = "" }) => {
  // The chapter breakdown is the whole summary, and this recounts on every
  // keystroke — so the markup comes off once, and only when the text changes.
  const key = (Array.isArray(parts) ? parts : [parts]).join("\u0000");
  const { words, characters } = React.useMemo(() => {
    const text = key
      .split("\u0000")
      .map(stripHtml)
      .join(" ");
    return {
      words: text.trim().split(/\s+/).filter(Boolean).length,
      characters: text.replace(/\s+/g, "").length,
    };
  }, [key]);

  return (
    <span
      className={`text-muted small ${className}`}
      title="Characters exclude spaces"
    >
      {format(words)} {words === 1 ? "word" : "words"} · {format(characters)}{" "}
      {characters === 1 ? "character" : "characters"}
    </span>
  );
};

/**
 * A field's label with its own count beside it.
 *
 * Every editor on the form carries one, because the section totals answer
 * "how long is the Overview" while a writer trimming one field needs to know
 * how long THAT field is.
 */
export const StatLabel = ({ label, parts }) => (
  <div className="d-flex align-items-baseline flex-wrap gap-2 mb-1">
    <label className="label mb-0">{label}</label>
    <TextStats parts={parts} />
  </div>
);

export default TextStats;
